import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Alert, Card, Typography } from "antd";
import { ArrowLeftOutlined, LockOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import useUserDocumentsQuery from "../../api/queries/useUserDocumentsQuery";
import DocumentTable from "./components/DocumentTable";
import DocumentDrawer from "./components/DocumentDrawer";

const { Title, Text } = Typography;

// Same card treatment as CompletedUsers/GuestDetails so this page sits in the same
// visual family as the rest of the dashboard.
const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
};

const toolbarStyle = {
  padding: "16px 24px",
  borderBottom: "1px solid #f0f0f0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
};

// A standalone route rather than another tab on UserDetails: that page is already a
// dense three-card layout, and this screen carries its own access notice which would
// be lost inside a tab.
const UserDocuments = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useUserDocumentsQuery(userId);
  const [selected, setSelected] = useState(null);

  const count = (data || []).length;

  return (
    <Layout>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/user-details/${userId}`)}
        style={{ marginBottom: 12, padding: 0, fontSize: 14 }}
      >
        Back to user
      </Button>

      <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
        <div style={toolbarStyle}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Customer documents
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {isLoading
                ? "Loading…"
                : `${count} document${count === 1 ? "" : "s"} in this customer's vault`}
            </Text>
          </div>

          {/* The audit notice is deliberately always visible rather than a one-time
              banner: it is the standing condition of using this screen, not a tip. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 8,
              background: "#f1f5f9",
              color: "#475569",
              fontSize: 12,
              lineHeight: "18px",
              maxWidth: 420,
            }}
          >
            <LockOutlined style={{ fontSize: 13, flexShrink: 0 }} />
            Read-only. Viewing or downloading a document is recorded in the audit log.
          </span>
        </div>

        {isError && (
          <div style={{ padding: 24 }}>
            <Alert type="error" showIcon message="Could not load documents." />
          </div>
        )}

        <DocumentTable
          documents={data}
          loading={isLoading}
          onOpen={(record) => setSelected(record)}
        />
      </Card>

      <DocumentDrawer
        document={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </Layout>
  );
};

export default UserDocuments;
